<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import SalesAPI from 'dashboard/api/sales';

const props = defineProps({
  periodParams: { type: Object, default: () => ({}) },
});

const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('auth/getCurrentUser');

const sales = ref([]);
const isLoading = ref(false);

const brl = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const formatDate = iso => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const activeSales = computed(() => sales.value.filter(s => s.status === 'active'));
const cancelledSales = computed(() => sales.value.filter(s => s.status === 'cancelled'));
const totalAmount = computed(() =>
  activeSales.value.reduce((sum, s) => sum + (s.amount ?? 0), 0)
);

async function loadSales() {
  isLoading.value = true;
  try {
    const { data } = await SalesAPI.get({
      agent_id: currentUser.value?.id,
      ...props.periodParams,
    });
    sales.value = data;
  } finally {
    isLoading.value = false;
  }
}

function goToConversation(conversationId) {
  router.push({
    name: 'inbox_conversation',
    params: { accountId: accountId.value, conversation_id: conversationId },
  });
}

onMounted(loadSales);
watch(() => props.periodParams, loadSales, { deep: true });
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Resumo -->
    <div v-if="!isLoading && sales.length" class="flex items-center gap-4 text-sm">
      <span class="font-medium text-n-slate-12">
        {{ activeSales.length }} {{ $t('SALES.MY_SALES.ACTIVE') }}
      </span>
      <span class="text-n-slate-12 font-semibold">{{ brl(totalAmount) }}</span>
      <span v-if="cancelledSales.length" class="text-n-slate-9">
        {{ cancelledSales.length }} {{ $t('SALES.MY_SALES.CANCELLED') }}
      </span>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center py-12">
      <span class="i-lucide-loader-circle animate-spin text-3xl text-n-slate-9" />
    </div>

    <!-- Empty -->
    <div
      v-else-if="!sales.length"
      class="flex flex-col items-center gap-2 py-12 text-n-slate-9"
    >
      <span class="i-lucide-shopping-bag text-4xl" />
      <p class="text-sm">{{ $t('SALES.AGENT_SALES.EMPTY') }}</p>
    </div>

    <!-- Lista -->
    <div v-else class="flex flex-col gap-2">
      <div
        v-for="sale in sales"
        :key="sale.id"
        class="rounded-xl border p-4 flex flex-col gap-3"
        :class="
          sale.status === 'active'
            ? 'border-n-strong bg-n-alpha-3'
            : 'border-n-weak bg-n-alpha-1 opacity-70'
        "
      >
        <!-- Status + valor -->
        <div class="flex items-center justify-between gap-2">
          <span
            class="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full"
            :class="
              sale.status === 'active'
                ? 'bg-n-alpha-3 text-n-slate-11'
                : 'bg-n-alpha-2 text-n-slate-9'
            "
          >
            <span
              :class="sale.status === 'active' ? 'i-lucide-check-circle' : 'i-lucide-x-circle'"
              class="text-[10px]"
            />
            {{
              sale.status === 'active'
                ? $t('SALES.INFO_PANEL.STATUS_ACTIVE')
                : $t('SALES.INFO_PANEL.STATUS_CANCELLED')
            }}
          </span>
          <span
            class="text-base font-bold"
            :class="sale.status === 'active' ? 'text-n-slate-12' : 'text-n-slate-9 line-through'"
          >
            {{ brl(sale.amount) }}
          </span>
        </div>

        <!-- Contato + conversa -->
        <div class="flex items-start justify-between gap-4">
          <div class="flex items-center gap-2 min-w-0">
            <span class="i-lucide-user text-sm text-n-slate-9 shrink-0" />
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ sale.contact_name || '—' }}
              </p>
              <p v-if="sale.contact_phone" class="text-xs text-n-slate-9">
                {{ sale.contact_phone }}
              </p>
            </div>
          </div>
          <button
            v-if="sale.conversation_id"
            class="flex items-center gap-1.5 text-xs text-n-brand hover:underline transition-colors shrink-0 mt-0.5"
            @click="goToConversation(sale.conversation_id)"
          >
            <span class="i-lucide-message-square text-sm" />
            {{ $t('SALES.AGENT_SALES.VIEW_CONVERSATION', { id: sale.conversation_id }) }}
          </button>
        </div>

        <!-- Tipo de venda -->
        <div v-if="sale.sale_type" class="flex items-center gap-1.5">
          <span class="i-lucide-tag text-xs text-n-slate-9" />
          <span class="text-xs text-n-slate-10">{{ sale.sale_type }}</span>
        </div>

        <!-- Pedido + data -->
        <div class="flex items-center gap-4 text-xs text-n-slate-10">
          <span v-if="sale.order_number" class="font-mono">{{ sale.order_number }}</span>
          <span>{{ formatDate(sale.created_at) }}</span>
        </div>

        <!-- Motivo cancelamento -->
        <div
          v-if="sale.status === 'cancelled' && sale.cancellation_reason"
          class="rounded-lg bg-n-alpha-3 border border-n-strong px-3 py-2 text-xs text-n-slate-11"
        >
          <span class="font-medium">{{ $t('SALES.INFO_PANEL.CANCELLATION_REASON') }}:</span>
          {{ sale.cancellation_reason }}
        </div>
      </div>
    </div>
  </div>
</template>
